<div class="container">

  <header class="page-heading">
    <h1>RD Calculator</h1>
    <p>Calculate the maturity value and interest earned on a bank Recurring Deposit (RD), compounded quarterly, with this online calculator.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">Recurring Deposit Details</div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('monthlyDeposit')">
        <label class="field-label" for="monthlyDeposit">Monthly Deposit</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="monthlyDeposit" type="number"
            [(ngModel)]="monthlyDeposit"
            [min]="C.monthlyDeposit.min" [max]="C.monthlyDeposit.max" [step]="C.monthlyDeposit.step"
            (ngModelChange)="onFieldChange('monthlyDeposit')"
            (blur)="onFieldBlur('monthlyDeposit')"
            placeholder="e.g. 5,000"
            autocomplete="off"/>
        </div>
        <div class="field-hint">{{ C.monthlyDeposit.min | currency:'INR':'symbol':'1.0-0':'en-IN' }} – {{ C.monthlyDeposit.max | currency:'INR':'symbol':'1.0-0':'en-IN' }} per month</div>
        @if (hasError('monthlyDeposit')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('monthlyDeposit') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('annualRate')">
        <label class="field-label" for="annualRate">Interest Rate</label>
        <div class="input-shell suffix">
          <input id="annualRate" type="number"
            [(ngModel)]="annualRate"
            [min]="C.rate.min" [max]="C.rate.max" [step]="C.rate.step"
            (ngModelChange)="onFieldChange('annualRate')"
            (blur)="onFieldBlur('annualRate')"
            placeholder="e.g. 6.5"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        @if (hasError('annualRate')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('annualRate') }}
          </div>
        }
      </div>
    </div>

    <div class="field-group" [class.has-error]="hasError('tenureMonths')">
      <label class="field-label" for="tenureMonths">Tenure</label>
      <div class="input-shell suffix">
        <input id="tenureMonths" type="number"
          [(ngModel)]="tenureMonths"
          [min]="C.tenureMonths.min" [max]="C.tenureMonths.max" [step]="C.tenureMonths.step"
          (ngModelChange)="onFieldChange('tenureMonths')"
          (blur)="onFieldBlur('tenureMonths')"
          placeholder="e.g. 24"
          autocomplete="off"/>
        <span class="affix right">Mo</span>
      </div>
      <div class="field-hint">Must be a multiple of 3 months. Interest is compounded quarterly.</div>
      @if (hasError('tenureMonths')) {
        <div class="field-error">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {{ fieldError('tenureMonths') }}
        </div>
      }
    </div>

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Calculate
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to calculate
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="summary-grid mb-md">
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">Maturity Value</div>
        <div class="sc-value">{{ fmt.currency(result.maturityValue) }}</div>
        <div class="sc-sub">after {{ tenureMonths }} months</div>
      </div>
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Total Deposited</div>
        <div class="sc-value">{{ fmt.currency(result.totalInvested) }}</div>
        <div class="sc-sub">{{ fmt.currency(monthlyDeposit) }}/mo</div>
      </div>
      <div class="summary-card c-red">
        <div class="sc-icon"></div>
        <div class="sc-label">Interest Earned</div>
        <div class="sc-value">{{ fmt.currency(result.totalInterest) }}</div>
        <div class="sc-sub">@ {{ annualRate }}% p.a., compounded quarterly</div>
      </div>
    </div>

    <div class="card charts-card mb-md">
      <div class="section-title">Visual Breakdown</div>
      <div class="charts-top-row">
        <div class="chart-panel">
          <div class="chart-panel-label">Deposited vs Interest</div>
          <div class="chart-canvas-wrap chart-canvas-pie">
            <canvas #breakdownRef></canvas>
          </div>
        </div>
        <div class="chart-panel chart-panel-bar">
          <div class="chart-panel-label">Balance Growth</div>
          <div class="chart-canvas-wrap">
            <canvas #growthRef></canvas>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Quarter-end Balance</div>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th class="t-left">Month</th>
              <th>Total Deposited</th>
              <th class="t-balance">Balance</th>
            </tr>
          </thead>
          <tbody>
            @for (row of result.months; track row.month) {
              @if (row.month % 3 === 0) {
                <tr>
                  <td class="t-left t-num">Month {{ row.month }}</td>
                  <td>{{ fmt.currency(row.invested) }}</td>
                  <td class="t-balance">{{ fmt.currency(row.balance) }}</td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">How is RD Maturity Value Calculated?</h2>
    <p>
      A Recurring Deposit (RD) lets you deposit a fixed amount every month into a bank account,
      earning interest that Indian banks typically compound quarterly on the running balance.
    </p>
    <p>
      This calculator simulates each monthly deposit and applies quarterly compounding, the same
      approach most Indian banks use to compute RD maturity value.
    </p>
    <p>
      For example, depositing &#8377;5,000 every month for 2 years at 6.5% p.a. matures to about
      &#8377;1.28 lakh, of which &#8377;1.2 lakh is your own deposit and the rest is interest earned.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>How is RD different from a Fixed Deposit?</h3>
      <p>
        An FD is a one-time lumpsum deposit, while an RD lets you build savings through fixed
        monthly instalments &mdash; useful for building a habit of regular saving.
      </p>
    </div>
    <div class="faq-item">
      <h3>Is RD interest taxable?</h3>
      <p>
        Yes. Interest earned on an RD is fully taxable as per your income tax slab, and banks
        deduct TDS if the interest earned in a year exceeds the prescribed threshold.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more: <a routerLink="/learn/fd-vs-rd-vs-ppf">FD vs RD vs PPF: where should each rupee go?</a>
    </p>
  </div>
</section>
